import { Spinner, Row, Col, Card } from "react-bootstrap";
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import { COLOR, STYLE } from '../../../../config/constraits';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useRef } from "react";
import { generateCsvFile } from "../../../../utils/downloadCsvFile";
import { saveBeforeFilter, addFilter } from "../../../../redux/filtersSlice";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ChartShareActions from "../../../../components/ChartShareActions";
import { RootState } from "../../../../redux/store";
import { useImprovementPlanPieChartStatus } from "../../hooks/useImprovementPlanPIeChartStatus";
import { AuditActionEnum } from "../../../auditLogs/types";
import { ImprovementPlanStatusPieChartDto } from "../../types";

interface ImprovementPlanStatusPieChartProps {
    setMessage: (msg: string) => void;
}
export default function ImprovementPlanStatusPieChart({ setMessage }: ImprovementPlanStatusPieChartProps) {
    const { t } = useTranslation('improvementPlan');
    const dispatch = useDispatch();
    const navigate = useNavigate();
    
    const { data, isLoading, error } = useImprovementPlanPieChartStatus();
    
    useEffect(() => {
        if (error) {
            const messageError = (error as any)?.response?.data?.message || (error as Error).message;
            setMessage(messageError);
        }
    }, [error, setMessage]);

    const { countInDefinition, countScheduled, countInProgress, countFinished, inDefinitionStatusPercentage, scheduledStatusPercentage,
        inProgressStatusPercentage, finishedStatusPercentage } = useMemo(() => {
        if (!data) {
            return {
                countInDefinition: 0,
                countScheduled: 0,
                countInProgress: 0,
                countFinished: 0,
                inDefinitionStatusPercentage: 0,
                scheduledStatusPercentage: 0,
                inProgressStatusPercentage: 0,
                finishedStatusPercentage: 0
            };
        }

        const countInDefinition = data.items.find((item) => item.status === 1)?.count ?? 0;
        const countScheduled = data.items.find((item) => item.status === 2)?.count ?? 0;
        const countInProgress = data.items.find((item) => item.status === 3)?.count ?? 0;
        const countFinished = data.items.find((item) => item.status === 4)?.count ?? 0;
        const safeTotal = data.total > 0 ? data.total : 1;

        return {
            countInDefinition: countInDefinition,
            countScheduled: countScheduled,
            countInProgress: countInProgress,
            countFinished: countFinished,
            inDefinitionStatusPercentage: (countInDefinition * 100) / safeTotal,
            scheduledStatusPercentage: (countScheduled * 100) / safeTotal,
            inProgressStatusPercentage: (countInProgress * 100) / safeTotal,
            finishedStatusPercentage: (countFinished * 100) / safeTotal
        };
    }, [data]);

    const isEmptyData = inDefinitionStatusPercentage + scheduledStatusPercentage + inProgressStatusPercentage + finishedStatusPercentage  === 0;

    const ChartData = useMemo(() => 
        isEmptyData
            ? [
                { name: t("improvementPlan:statusPieChart.labelInDefinition"), value: 100, fixedValue: '0', color: '#BFBFBF', valueInt: "0" },
                { name: t("improvementPlan:statusPieChart.labelScheduled"), value: 100, fixedValue: '0', color: '#BFBFBF', valueInt: "1" },
                { name: t("improvementPlan:statusPieChart.labelInProgress"), value: 100, fixedValue: '0', color: '#BFBFBF', valueInt: "1" },
                { name: t("improvementPlan:statusPieChart.labelFinished"), value: 100, fixedValue: '0', color: '#BFBFBF', valueInt: "2" },
            ]
            : [
                { name: t("improvementPlan:statusPieChart.labelInDefinition"), value: inDefinitionStatusPercentage, fixedValue: inDefinitionStatusPercentage.toFixed(), color: "#6258CC", valueInt: "1" },
                { name: t("improvementPlan:statusPieChart.labelScheduled"), value: scheduledStatusPercentage, fixedValue: scheduledStatusPercentage.toFixed(), color: "#C0A3FF", valueInt: "2" },
                { name: t("improvementPlan:statusPieChart.labelInProgress"), value: inProgressStatusPercentage, fixedValue: inProgressStatusPercentage.toFixed(), color:"#FFC876", valueInt: "3" },
                { name: t("improvementPlan:statusPieChart.labelFinished"), value: finishedStatusPercentage, fixedValue: finishedStatusPercentage.toFixed(), color: COLOR.VERD, valueInt: "4" },
            ],
        [isEmptyData, inDefinitionStatusPercentage, scheduledStatusPercentage, inProgressStatusPercentage, finishedStatusPercentage, t]);

    const handleSegmentClick = (e: string) => {
        const filter = {
            name: 'Status',
            values: [e]
        };
        dispatch(saveBeforeFilter('improvementPlans'));
        dispatch(addFilter({ table: 'improvementPlans', filter: filter }));
        navigate('/improvement-plans/historic');
    };

    const statuschartRef = useRef<HTMLDivElement>(null);
    const selectedFilters = useSelector((state: RootState) => state.filters.improvementPlans.selectedFilters);

    const exportPieChartCsvDataDto: ImprovementPlanStatusPieChartDto = {
        countInDefinition: countInDefinition,
        countScheduled: countScheduled,
        countInProgress: countInProgress,
        countFinished: countFinished,
        inDefinitionStatusPercentage: inDefinitionStatusPercentage,
        scheduledStatusPercentage: scheduledStatusPercentage,
        inProgressStatusPercentage: inProgressStatusPercentage,
        finishedStatusPercentage: finishedStatusPercentage,
    };

    const exportPieChartCsvData = async (): Promise<void> => {
        const rows = [
            {
                state: t("improvementPlan:statusPieChart.labelInDefinition"),
                improvementPlans: exportPieChartCsvDataDto.countInDefinition ?? 0,
                percentage: exportPieChartCsvDataDto.inDefinitionStatusPercentage ?? 0,
            },
            {
                state: t("improvementPlan:statusPieChart.labelScheduled"),
                improvementPlans: exportPieChartCsvDataDto.countScheduled ?? 0,
                percentage: exportPieChartCsvDataDto.scheduledStatusPercentage ?? 0,
            },
            {
                state: t("improvementPlan:statusPieChart.labelInProgress"),
                improvementPlans: exportPieChartCsvDataDto.countInProgress ?? 0,
                percentage: exportPieChartCsvDataDto.inProgressStatusPercentage ?? 0,
            },
            {
                state: t("improvementPlan:statusPieChart.labelFinished"),
                improvementPlans: exportPieChartCsvDataDto.countFinished ?? 0,
                percentage: exportPieChartCsvDataDto.finishedStatusPercentage ?? 0,
            },
        ];

        generateCsvFile({
            headers: [
                t("claim:statusPieChart.export.headerState"),
                t("claim:statusPieChart.export.headerImprovementPlans"),
                t("claim:statusPieChart.export.headerPercentage"),
            ],
            rows,
            mapRow: (item) => [item.state, item.improvementPlans, Number(item.percentage.toFixed(2))],
            fileBaseName: "improvement_plans_status_distribution",
            trailingSemicolon: true,
        });
    };


    return (
        <>
            {isLoading && (
                <div className="overlay-spinner">
                    <Spinner animation="border" variant="light" />
                </div>
            )}
            <div ref={statuschartRef}>
                <Card
                    style={{
                        backgroundColor: "#F5F5F5",
                        border: "none",
                        borderRadius: STYLE.BORDER_RADIUS,
                    }}
                    className="p-3 d-flex justify-content-center align-items-center text-center"
                >
                    <div className="d-flex justify-content-between align-items-center mb-3 w-100">
                        <h4 className="mb-0">{t("improvementPlan:statusPieChart.labelTitle")}</h4>
                        <ChartShareActions
                            chartRef={statuschartRef}
                            title={t("improvementPlan:statusPieChart.labelTitle")}
                            filters={selectedFilters}
                            t={t}
                            exportCsvAction={exportPieChartCsvData}
                            exportCsvAudit={{
                                entity: "DashboardIndicator",
                                action: AuditActionEnum.ExportCsv,
                                source: window.location.pathname,
                                description: "CSV export of improvement plan status pie chart",
                                filters: selectedFilters
                            }}
                        />
                    </div>
                    <ResponsiveContainer width="100%" height={280}>
                        <PieChart width={200} height={200}>
                            <Pie 
                                data={ChartData} 
                                dataKey="value" 
                                nameKey="name" 
                                innerRadius={84} 
                                outerRadius={120} 
                                startAngle={90} 
                                endAngle={-270} 
                                fill="#82ca9d"
                            >
                                {ChartData.map((entry, index) => (
                                    <Cell 
                                        key={`cell-${index}`} 
                                        fill={entry.color} 
                                        style={{ cursor: "pointer" }} 
                                        onClick={() => handleSegmentClick(entry.valueInt)} 
                                    />
                                ))}
                            </Pie>
                        </PieChart>
                    </ResponsiveContainer>
                    <Row>
                        <Col className="d-flex justify-content-center">
                            <div>
                                {ChartData.map((item, index) => (
                                    <div key={index} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                                        <span 
                                            className="rounded-circle" 
                                            style={{ 
                                                width: '10px', 
                                                height: '10px', 
                                                display: 'inline-block', 
                                                marginRight: '8px', 
                                                backgroundColor: item.color 
                                            }}
                                        />
                                        <p style={{ margin: 0 }}>
                                            {item.name}: {isNaN(Number(item.fixedValue)) ? '0' : item.fixedValue}%
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </Col>
                    </Row>
                </Card>
            </div>
        </>
    );
}
